import React from 'react';
import { User, Code, Zap, Target, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

const About = ({ data }) => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const highlights = [
    {
      icon: <Code className="h-6 w-6" />,
      title: "Technical Excellence",
      description: data.expertise
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Experience Range",
      description: data.experience
    },
    {
      icon: <Target className="h-6 w-6" />,
      title: "Product Mindset",
      description: data.mindset
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="about" className="py-24 relative overflow-hidden">
      {/* Subtle Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/30 to-slate-900/20" />
        <div className="absolute top-32 right-20 w-64 h-64 bg-slate-400/3 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-slate-500/2 rounded-full blur-3xl" />
      </div>
      
      <div className="container mx-auto px-6 relative z-10" ref={ref}>
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ y: 30, opacity: 0 }}
          animate={inView ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <User className="h-8 w-8 text-slate-400" />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50">
              About Me
            </h2>
          </div>
          <motion.div 
            className="w-16 h-0.5 bg-slate-500 mx-auto mb-8"
            initial={{ width: 0 }}
            animate={inView ? { width: 64 } : { width: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          />
          <p className="text-xl text-slate-300 leading-relaxed max-w-4xl mx-auto">
            {data.summary}
          </p>
        </motion.div>

        {/* Three Column Layout */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {/* Professional */}
          <motion.div
            className="lg:col-span-2 space-y-8"
            initial={{ x: -30, opacity: 0 }}
            animate={inView ? { x: 0, opacity: 1 } : { x: -30, opacity: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 p-8 rounded-2xl">
              <h3 className="text-xl font-semibold text-slate-50 mb-4 flex items-center gap-2">
                <Code className="h-5 w-5 text-slate-400" />
                Professional
              </h3>
              <p className="text-slate-300 leading-relaxed mb-6">
                {data.experience}
              </p>
              <p className="text-slate-300 leading-relaxed">
                {data.expertise}
              </p>
            </div>
            
            <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 p-8 rounded-2xl">
              <h3 className="text-xl font-semibold text-slate-50 mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-slate-400" />
                Personal
              </h3>
              <p className="text-slate-300 leading-relaxed">
                {data.personal}
              </p>
            </div>
          </motion.div>

          {/* Achievements */}
          <motion.div
            className="space-y-6"
            initial={{ x: 30, opacity: 0 }}
            animate={inView ? { x: 0, opacity: 1 } : { x: 30, opacity: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 p-6 rounded-2xl">
              <h3 className="text-xl font-semibold text-slate-50 mb-6 flex items-center gap-2">
                <Target className="h-5 w-5 text-slate-400" />
                Key Work
              </h3>
              <div className="space-y-3">
                {data.metrics.map((metric, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-xl border border-slate-700/20"
                    initial={{ opacity: 0, y: 10 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                    transition={{ delay: 0.6 + index * 0.1, duration: 0.5 }}
                  >
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-2 flex-shrink-0" />
                    <p className="text-slate-300 text-sm leading-relaxed">{metric}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

      </div>
    </section>
  );
};

export default About;